import {
  reorderCadenceSchema,
  customerCadenceSettingsSchema,
  customerCareInsightSchema,
  customerContactPreferenceOverviewSchema,
  customerContactPreferenceSchema,
  customerSalesRuleExclusionListSchema,
  salesAssistantSavedViewListSchema,
  salesAssistantSavedViewSchema,
  salesAssistantAnalyticsResponseSchema,
  salesAssigneeListSchema,
  salesBulkOperationResultSchema,
  salesCockpitRecommendationListResponseSchema,
  salesCockpitSummarySchema,
  salesCallPreparationSchema,
  salesRecommendationSchema,
  salesTaskListResponseSchema,
  salesTaskSchema,
  salesTeamListResponseSchema,
  type ReorderCadence,
  type CustomerCadenceSettings,
  type CustomerCadenceSettingsWriteValues,
  type CustomerCareInsight,
  type CustomerContactPreference,
  type CustomerContactPreferenceOverview,
  type CustomerContactPreferenceWriteValues,
  type CustomerSalesRuleExclusion,
  type CustomerSalesRuleExclusionWriteValues,
  type SalesAssistantSavedView,
  type SalesAssistantSavedViewWriteValues,
  type SalesAssistantAnalyticsResponse,
  type SalesAssistantAnalyticsScope,
  type SalesBulkRecommendationAssignValues,
  type SalesBulkRecommendationSnoozeValues,
  type SalesCockpitCategory,
  type SalesCockpitRecommendationListQuery,
  type SalesCockpitScope,
  type SalesCockpitSummary,
  type SalesCallPreparation,
  type SalesRecommendation,
  type SalesTask,
  type SalesTaskCreateValues,
  type SalesTaskOutcomeWriteValues,
  type SalesTaskStatus,
  type SalesTaskSummary,
  type SalesTeam,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, apiSend, type ApiError } from '@/lib/api-client';

export const salesAssistantKeys = {
  all: ['sales-assistant'] as const,
  cadence: (customerId: string) => ['sales-assistant', 'cadence', customerId] as const,
  cadenceSettings: (customerId: string) =>
    ['sales-assistant', 'cadence-settings', customerId] as const,
  customerInsight: (customerId: string, scope: SalesCockpitScope) =>
    ['sales-assistant', 'customer-insight', customerId, scope] as const,
  customerExclusions: (customerId: string) =>
    ['sales-assistant', 'customer-exclusions', customerId] as const,
  contactPreferences: (customerId: string) =>
    ['sales-assistant', 'contact-preferences', customerId] as const,
  recommendations: (params: SalesCockpitListParams) =>
    ['sales-assistant', 'recommendations', params] as const,
  summary: (scope: SalesCockpitScope) => ['sales-assistant', 'summary', scope] as const,
  tasks: (params: SalesTaskListParams) => ['sales-assistant', 'tasks', params] as const,
  task: (taskId: string) => ['sales-assistant', 'task', taskId] as const,
  savedViews: ['sales-assistant', 'saved-views'] as const,
  assignees: ['sales-assistant', 'assignees'] as const,
  teams: ['sales-assistant', 'teams'] as const,
  analytics: (params: SalesAssistantAnalyticsParams) =>
    ['sales-assistant', 'analytics', params] as const,
};

export interface SalesAssistantAnalyticsParams {
  scope: SalesAssistantAnalyticsScope;
  dateFrom: string;
  dateTo: string;
  page?: number;
  pageSize?: number;
  teamId?: string | undefined;
  userId?: string | undefined;
}

function searchString(values: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  return search.toString();
}

export function useSalesAssistantAnalytics(
  params: SalesAssistantAnalyticsParams,
): UseQueryResult<SalesAssistantAnalyticsResponse, ApiError> {
  const query = searchString({ page: 1, pageSize: 20, ...params });
  return useQuery({
    queryKey: salesAssistantKeys.analytics(params),
    queryFn: () =>
      apiFetch(`/api/v1/sales-assistant/analytics?${query}`, salesAssistantAnalyticsResponseSchema),
  });
}

export function useCustomerCadence(customerId: string): UseQueryResult<ReorderCadence, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.cadence(customerId),
    queryFn: () =>
      apiFetch(`/api/v1/sales-assistant/customers/${customerId}/cadence`, reorderCadenceSchema),
    enabled: customerId !== '',
  });
}

export function useCustomerCadenceSettings(
  customerId: string,
): UseQueryResult<CustomerCadenceSettings, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.cadenceSettings(customerId),
    queryFn: () =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/cadence-settings`,
        customerCadenceSettingsSchema,
      ),
    enabled: customerId !== '',
  });
}

export function useUpdateCustomerCadenceSettings(): UseMutationResult<
  CustomerCadenceSettings,
  ApiError,
  { customerId: string; input: CustomerCadenceSettingsWriteValues }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, input }) =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/cadence-settings`,
        customerCadenceSettingsSchema,
        { method: 'PUT', body: input },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: salesAssistantKeys.cadence(variables.customerId),
      });
      void queryClient.invalidateQueries({
        queryKey: salesAssistantKeys.cadenceSettings(variables.customerId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['sales-assistant', 'customer-insight', variables.customerId],
      });
    },
  });
}

export function useCustomerCareInsight(
  customerId: string,
  scope: SalesCockpitScope = 'OWN',
): UseQueryResult<CustomerCareInsight, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.customerInsight(customerId, scope),
    queryFn: () =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/insight?scope=${scope}`,
        customerCareInsightSchema,
      ),
    enabled: customerId !== '',
  });
}

export function useCustomerContactPreferences(
  customerId: string,
  enabled = true,
): UseQueryResult<CustomerContactPreferenceOverview, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.contactPreferences(customerId),
    queryFn: () =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/contact-preferences`,
        customerContactPreferenceOverviewSchema,
      ),
    enabled: enabled && customerId !== '',
  });
}

export function useSaveCustomerContactPreference(): UseMutationResult<
  CustomerContactPreference,
  ApiError,
  {
    customerId: string;
    contactId: string | null;
    input: CustomerContactPreferenceWriteValues;
  }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, contactId, input }) =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}${
          contactId === null ? '' : `/contacts/${contactId}`
        }/contact-preferences`,
        customerContactPreferenceSchema,
        { method: 'PUT', body: input },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: salesAssistantKeys.contactPreferences(variables.customerId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['sales-assistant', 'customer-insight', variables.customerId],
      });
      invalidateCockpit(queryClient);
    },
  });
}

export function useDeleteCustomerContactPreference(): UseMutationResult<
  void,
  ApiError,
  { customerId: string; contactId: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, contactId }) =>
      apiSend(
        `/api/v1/sales-assistant/customers/${customerId}${
          contactId === null ? '' : `/contacts/${contactId}`
        }/contact-preferences`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: salesAssistantKeys.contactPreferences(variables.customerId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['sales-assistant', 'customer-insight', variables.customerId],
      });
      invalidateCockpit(queryClient);
    },
  });
}

export function useSalesCallPreparation(customerId: string) {
  return useQuery({
    queryKey: ['sales-assistant', 'call-preparation', customerId],
    queryFn: ({ signal }) =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/call-preparation`,
        salesCallPreparationSchema,
        { method: 'POST', signal },
      ),
    enabled: Boolean(customerId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}
export function usePrepareSalesCall(): UseMutationResult<
  SalesCallPreparation,
  ApiError,
  { customerId: string; locale: string }
> {
  return useMutation({
    mutationFn: ({ customerId, locale }) =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/call-preparation`,
        salesCallPreparationSchema,
        { method: 'POST', body: { includeAi: true, locale } },
      ),
  });
}

export function useCustomerSalesRuleExclusions(
  customerId: string,
): UseQueryResult<{ data: CustomerSalesRuleExclusion[] }, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.customerExclusions(customerId),
    queryFn: () =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/rule-exclusions`,
        customerSalesRuleExclusionListSchema,
      ),
    enabled: customerId !== '',
  });
}

export function useReplaceCustomerSalesRuleExclusions(): UseMutationResult<
  { data: CustomerSalesRuleExclusion[] },
  ApiError,
  { customerId: string; input: CustomerSalesRuleExclusionWriteValues }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, input }) =>
      apiFetch(
        `/api/v1/sales-assistant/customers/${customerId}/rule-exclusions`,
        customerSalesRuleExclusionListSchema,
        { method: 'PUT', body: input },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: salesAssistantKeys.customerExclusions(variables.customerId),
      });
      invalidateCockpit(queryClient);
    },
  });
}

export interface SalesCockpitListParams {
  page: number;
  pageSize: number;
  scope: SalesCockpitScope;
  category: SalesCockpitCategory;
  status: SalesCockpitRecommendationListQuery['status'];
  priority?: SalesCockpitRecommendationListQuery['priority'];
  type?: SalesCockpitRecommendationListQuery['type'];
  assignedUserId?: string | undefined;
  assignedTeamId?: string | undefined;
  q?: string | undefined;
  sort: SalesCockpitRecommendationListQuery['sort'];
}

export function useSalesCockpitRecommendations(params: SalesCockpitListParams) {
  const query = searchString({
    ...params,
    status: params.status.join(','),
    priority: params.priority?.join(','),
    type: params.type?.join(','),
  });
  return useQuery({
    queryKey: salesAssistantKeys.recommendations(params),
    queryFn: () =>
      apiFetch(
        `/api/v1/sales-assistant/recommendations?${query}`,
        salesCockpitRecommendationListResponseSchema,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useSalesCockpitSummary(
  scope: SalesCockpitScope,
  enabled = true,
): UseQueryResult<SalesCockpitSummary, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.summary(scope),
    queryFn: () =>
      apiFetch(`/api/v1/sales-assistant/cockpit-summary?scope=${scope}`, salesCockpitSummarySchema),
    enabled,
  });
}

export interface SalesTaskListParams {
  customerId?: string | undefined;
  taskId?: string | undefined;
  page: number;
  pageSize: number;
  scope: SalesCockpitScope;
  status?: SalesTaskStatus[] | undefined;
  q?: string | undefined;
  dueBefore?: string | undefined;
  dueAfter?: string | undefined;
  activityType?:
    | 'CALL'
    | 'CALLBACK'
    | 'VISIT'
    | 'MEETING'
    | 'EMAIL'
    | 'QUOTE'
    | 'FOLLOW_UP'
    | 'COMPLAINT'
    | 'OTHER'
    | undefined;
  sort?: 'dueAt:asc' | 'dueAt:desc' | 'priority:desc' | 'createdAt:desc' | undefined;
}

export function useSalesTasks(params: SalesTaskListParams) {
  const query = searchString({
    ...params,
    status: params.status?.join(','),
    sort: params.sort ?? 'dueAt:asc',
  });
  return useQuery({
    queryKey: salesAssistantKeys.tasks(params),
    queryFn: () => apiFetch(`/api/v1/sales-assistant/tasks?${query}`, salesTaskListResponseSchema),
    placeholderData: (previous) => previous,
  });
}

export function useSalesTask(taskId: string, enabled = true): UseQueryResult<SalesTask, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.task(taskId),
    queryFn: () => apiFetch(`/api/v1/sales-assistant/tasks/${taskId}`, salesTaskSchema),
    enabled: enabled && taskId !== '',
  });
}

function invalidateCockpit(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: salesAssistantKeys.all });
}

export function useCreateSalesTask(): UseMutationResult<
  SalesTask,
  ApiError,
  SalesTaskCreateValues
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch('/api/v1/sales-assistant/tasks', salesTaskSchema, { method: 'POST', body: input }),
    onSuccess: () => invalidateCockpit(queryClient),
  });
}

export function useRecordSalesTaskOutcome(): UseMutationResult<
  SalesTask,
  ApiError,
  { taskId: string; input: SalesTaskOutcomeWriteValues }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, input }) =>
      apiFetch(`/api/v1/sales-assistant/tasks/${taskId}/outcome`, salesTaskSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => invalidateCockpit(queryClient),
  });
}

export function useSnoozeSalesRecommendation(): UseMutationResult<
  SalesRecommendation,
  ApiError,
  { recommendationId: string; snoozedUntil: string; reason: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recommendationId, ...body }) =>
      apiFetch(
        `/api/v1/sales-assistant/recommendations/${recommendationId}/snooze`,
        salesRecommendationSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCockpit(queryClient),
  });
}

export function useBulkAssignSalesRecommendations(): UseMutationResult<
  { updated: number },
  ApiError,
  SalesBulkRecommendationAssignValues
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch(
        '/api/v1/sales-assistant/recommendations/bulk-assign',
        salesBulkOperationResultSchema,
        { method: 'POST', body: input },
      ),
    onSuccess: () => invalidateCockpit(queryClient),
  });
}

export function useBulkSnoozeSalesRecommendations(): UseMutationResult<
  { updated: number },
  ApiError,
  SalesBulkRecommendationSnoozeValues
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch(
        '/api/v1/sales-assistant/recommendations/bulk-snooze',
        salesBulkOperationResultSchema,
        { method: 'POST', body: input },
      ),
    onSuccess: () => invalidateCockpit(queryClient),
  });
}

export function useSalesAssistantSavedViews(): UseQueryResult<
  { data: SalesAssistantSavedView[] },
  ApiError
> {
  return useQuery({
    queryKey: salesAssistantKeys.savedViews,
    queryFn: () =>
      apiFetch('/api/v1/sales-assistant/saved-views', salesAssistantSavedViewListSchema),
  });
}

export function useSaveSalesAssistantView(): UseMutationResult<
  SalesAssistantSavedView,
  ApiError,
  { id?: string | undefined; input: SalesAssistantSavedViewWriteValues }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiFetch(
        id === undefined
          ? '/api/v1/sales-assistant/saved-views'
          : `/api/v1/sales-assistant/saved-views/${id}`,
        salesAssistantSavedViewSchema,
        { method: id === undefined ? 'POST' : 'PUT', body: input },
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: salesAssistantKeys.savedViews }),
  });
}

export function useDeleteSalesAssistantView(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiSend(`/api/v1/sales-assistant/saved-views/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: salesAssistantKeys.savedViews }),
  });
}

export function useSalesAssignees(): UseQueryResult<
  { data: Array<{ id: string; name: string }> },
  ApiError
> {
  return useQuery({
    queryKey: salesAssistantKeys.assignees,
    queryFn: () => apiFetch('/api/v1/sales-assistant/assignees', salesAssigneeListSchema),
  });
}

export function useSalesTeams(): UseQueryResult<{ data: SalesTeam[] }, ApiError> {
  return useQuery({
    queryKey: salesAssistantKeys.teams,
    queryFn: () => apiFetch('/api/v1/sales-assistant/teams', salesTeamListResponseSchema),
  });
}

export type { SalesTaskSummary };
